import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Card, Chip, EmptyState, LineChart, Meter, PageHeader, PageLoading, Segmented, Select, Skeleton, type ChartBand, type ChartRule } from '@koyomi/ui';
import { api, ApiError } from '../../lib/api.ts';
import { adminKey, money, num, useAdminQuery, useAdminSave, when } from '../../lib/queries.ts';
import { InfraSettingsPanel } from './InfraSettingsPanel.tsx';
import { fmtNum, fmtUnit, fmtUptime, getBand, i18nPath, isEnglish, LOWER_WORSE, QualityChip, STATUS_RANK, StatusChip, statusTone, TREND_METRICS, usd, worstStatus } from './infra-ui.tsx';
import type { InfraGroup, InfraHistory, InfraLlm, InfraMetric, InfraRecommendation, InfraSettings, InfraSnapshot, InfraStatus, Thresholds } from './types.ts';
import './infra.css';

type Tab = 'monitor' | 'settings';
const GROUP_ORDER = ['api', 'postgres', 'redis', 'queues', 'product', 'llm', 'meta'] as const;
const SEVERITY_RANK: Record<InfraRecommendation['severity'], number> = { red: 2, yellow: 1, info: 0 };

/** Painel de infraestrutura: semáforo, latência, filas, processos, LLM, capacidade, tendências e recomendações. */
export function InfraPage() {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const lang = i18n.language;
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('monitor');
  // 90 s: cada coleta são 13 chamadas ao Render; a cada 30 s esbarrava no limite por minuto dos endpoints de métricas.
  const q = useAdminQuery<InfraSnapshot>('/admin/infra', undefined, { refetchInterval: 90_000 });
  const settings = useAdminQuery<InfraSettings>('/admin/infra/settings');
  const refresh = useAdminSave(() => api<InfraSnapshot>('/admin/infra/refresh', { method: 'POST' }), [], { silent: true });
  // Intervalo mínimo entre atualizações forçadas (a API também impõe 60 s): contagem regressiva no botão.
  const [waitSec, setWaitSec] = useState(0);
  useEffect(() => { if (waitSec <= 0) return; const id = setInterval(() => setWaitSec((n) => (n > 1 ? n - 1 : 0)), 1000); return () => clearInterval(id); }, [waitSec]);
  const doRefresh = () => refresh.mutate(undefined, {
    onSuccess: (data) => { qc.setQueryData(adminKey('/admin/infra', undefined), data); setWaitSec(60); },
    onError: (err) => { if (err instanceof ApiError && err.code === 'RATE_LIMITED') setWaitSec(Number((err.details as { retryAfterSec?: number } | undefined)?.retryAfterSec ?? 60)); },
  });
  const header = (
    <PageHeader
      title={t('admin:infra.title')}
      subtitle={t('admin:infra.subtitle')}
      actions={<>
        <Segmented value={tab} onChange={setTab} ariaLabel={t('admin:infra.title')} options={[{ value: 'monitor', label: t('admin:infra.tabs.monitor') }, { value: 'settings', label: t('admin:infra.tabs.settings') }]} />
        {tab === 'monitor' && <Button variant="primary" loading={refresh.isPending} disabled={waitSec > 0} onClick={doRefresh}>{waitSec > 0 ? t('admin:infra.refreshWait', { s: waitSec }) : t('admin:infra.refreshNow')}</Button>}
      </>}
    />
  );
  if (tab === 'settings') return <>{header}<InfraSettingsPanel query={settings} /></>;
  if (q.isLoading) return <>{header}<PageLoading cards={6} rows={4} /></>;
  const d = q.data;
  if (!d) return <>{header}<EmptyState title={t('admin:infra.loadError')} description={q.error instanceof ApiError ? q.error.message : undefined} action={<Button onClick={() => void q.refetch()}>{t('admin:infra.retry')}</Button>} /></>;
  const groups = GROUP_ORDER.map((k) => d.groups.find((g) => g.key === k)).filter((g): g is InfraGroup => !!g).concat(d.groups.filter((g) => !(GROUP_ORDER as readonly string[]).includes(g.key)));
  const th = settings.data?.thresholds;
  return (
    <>
      {header}
      <div className="k-stack">
        <Summary snapshot={d} groups={groups} fetching={q.isFetching} />
        <div className="k-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 290px), 1fr))' }}>
          {groups.map((g) => <GroupCard key={g.key} group={g} />)}
        </div>
        <div className="k-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))' }}>
          <LatencyCard snapshot={d} />
          <CapacityCard snapshot={d} />
        </div>
        <QueuesCard snapshot={d} />
        <div className="k-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))' }}>
          <LlmCard llm={d.llm} thresholds={th} />
          <ResourcesCard snapshot={d} />
        </div>
        <ProcessesCard snapshot={d} />
        <TrendsCard thresholds={th} />
        <div className="k-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))' }}>
          <ProjectionsCard snapshot={d} />
          <RecommendationsCard recommendations={d.recommendations} />
        </div>
        <MetaCard snapshot={d} />
        <p className="k-muted" style={{ fontSize: 12, margin: 0 }}>{t('admin:infra.autoRefresh')} · {when(d.takenAt, lang, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })}</p>
      </div>
    </>
  );
}

function Summary({ snapshot: d, groups, fetching }: { snapshot: InfraSnapshot; groups: InfraGroup[]; fetching: boolean }) {
  const { t, i18n } = useTranslation('admin');
  const overall = worstStatus(groups.map((g) => g.status));
  const unknown = groups.filter((g) => g.status === 'unknown').length;
  const counts = (['red', 'yellow', 'green'] as const).map((s) => [s, groups.filter((g) => g.status === s).length] as const).filter(([, n]) => n > 0);
  return (
    <div className="k-stack" style={{ gap: 12 }}>
      <Card tone={overall === 'red' ? 'danger' : overall === 'yellow' ? 'warn' : 'default'}>
        <div className="k-row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div className="k-row" style={{ gap: 12, flexWrap: 'wrap', minWidth: 0 }}>
            <StatusChip status={overall} />
            <strong style={{ fontSize: 16 }}>{t(`infra.overall.${overall}`)}</strong>
            <span className="k-muted" style={{ fontSize: 13 }}>{counts.map(([s, n]) => `${n} ${t(`infra.status.${s}`).toLowerCase()}`).join(' · ')}{unknown ? `${counts.length ? ' · ' : ''}${t('infra.unknownGroups', { count: unknown })}` : ''}</span>
          </div>
          <span className="k-muted" style={{ fontSize: 13 }}>
            {t('infra.takenAt', { when: when(d.takenAt, i18n.language, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }) })}
            {fetching && <span className="k-spinner" style={{ display: 'inline-block', marginLeft: 8, verticalAlign: 'middle' }} aria-label="loading" />}
          </span>
        </div>
      </Card>
      {!d.render.configured && (
        <Card tone="soft">
          <div className="k-row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <span style={{ fontSize: 14 }}>{t('infra.renderNotConfigured')}</span>
            <Link to="/integracoes">{t('infra.goToIntegrations')} ›</Link>
          </div>
        </Card>
      )}
      {d.render.configured && d.render.error && <Card tone="warn"><span style={{ fontSize: 13, overflowWrap: 'anywhere' }}>{t('infra.renderError')}: {d.render.error}</span></Card>}
    </div>
  );
}

function MetricRow({ m }: { m: InfraMetric }) {
  const { t, i18n } = useTranslation('admin');
  const lang = i18n.language;
  const label = isEnglish(lang) ? m.labelEn || m.label : m.label;
  const value = fmtUnit(m.value, m.unit, lang, m.text);
  const hasLimit = m.limit != null && m.limit > 0 && m.value != null && m.unit !== 'text';
  const limitText = hasLimit ? fmtUnit(m.limit, m.unit, lang) : null;
  return (
    <div className="k-infra-metric">
      <div className="k-row" style={{ justifyContent: 'space-between', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <span className="k-infra-metric__label">{label}</span>
        <span className="k-row" style={{ gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <strong className="k-infra-metric__value">{value}{limitText && <span className="k-muted" style={{ fontWeight: 400 }}> / {limitText}</span>}</strong>
          {m.status !== 'green' && <StatusChip status={m.status} />}
        </span>
      </div>
      {hasLimit && <Meter pct={((m.value ?? 0) / (m.limit ?? 1)) * 100} tone={statusTone(m.status)} label={`${label}: ${value} / ${limitText}`} />}
      {m.reason && <div className="k-infra-metric__reason">{m.reason}</div>}
      {!m.reason && m.status === 'unknown' && <div className="k-infra-metric__reason">{t('infra.noData')}</div>}
    </div>
  );
}

function GroupCard({ group }: { group: InfraGroup }) {
  const { t } = useTranslation('admin');
  return (
    <Card className={`k-infra-group k-infra-group--${group.status}`} title={t(`infra.groups.${group.key}`, { defaultValue: group.key })} actions={<StatusChip status={group.status} />}>
      {group.metrics.length === 0 ? <span className="k-muted" style={{ fontSize: 13 }}>{t('infra.noData')}</span> : <div className="k-stack" style={{ gap: 12 }}>{group.metrics.map((m) => <MetricRow key={m.key} m={m} />)}</div>}
    </Card>
  );
}

function LatencyCard({ snapshot: d }: { snapshot: InfraSnapshot }) {
  const { t, i18n } = useTranslation('admin');
  const lang = i18n.language;
  const l = d.latency;
  const metric = d.groups.flatMap((g) => g.metrics).find((m) => m.thresholdKey === 'replyLatencyP95Sec');
  return (
    <Card title={t('infra.latency.title')} subtitle={t('infra.latency.window', { minutes: l.windowMinutes, count: l.replies, n: num(l.replies, lang) })} actions={metric && <StatusChip status={metric.status} />}>
      <div className="k-infra-latency">
        <div className="k-infra-latency__hero">
          <div className="k-stat__label">{t('infra.latency.reply')}</div>
          <div className="k-infra-latency__value">{fmtUnit(l.replyP95Sec, 's', lang)} <span className="k-muted">p95</span></div>
          <div className="k-stat__hint">p50 {fmtUnit(l.replyP50Sec, 's', lang)}</div>
        </div>
        <div>
          <div className="k-stat__label">{t('infra.latency.model')}</div>
          <div className="k-infra-latency__value k-infra-latency__value--sm">{fmtUnit(l.modelP95Sec, 's', lang)} <span className="k-muted">p95</span></div>
          <div className="k-stat__hint">p50 {fmtUnit(l.modelP50Sec, 's', lang)}</div>
        </div>
      </div>
      <p className="k-infra-note">{t('infra.latency.customerFeels')}</p>
      {l.replies === 0 && <p className="k-muted" style={{ fontSize: 12, margin: '6px 0 0' }}>{t('infra.latency.noReplies')}</p>}
    </Card>
  );
}

function CapacityCard({ snapshot: d }: { snapshot: InfraSnapshot }) {
  const { t, i18n } = useTranslation('admin');
  const lang = i18n.language;
  const c = d.capacity;
  const tone = c.headroomPct == null ? 'default' : c.headroomPct < 20 ? 'danger' : c.headroomPct < 40 ? 'warn' : 'default';
  return (
    <Card title={t('infra.capacity.title')} tone={tone}>
      {c.ceilingRpm == null ? (
        <p style={{ margin: 0, fontSize: 14 }}>{t('infra.capacity.insufficient')}</p>
      ) : (
        <div className="k-stack" style={{ gap: 10 }}>
          <p style={{ margin: 0, fontSize: 15 }}>
            {t('infra.capacity.ceiling', { rpm: fmtNum(c.ceilingRpm, lang, 0) })}{' '}
            {c.peakRpm7d != null ? t('infra.capacity.peak', { rpm: fmtNum(c.peakRpm7d, lang, 1) }) : t('infra.capacity.noPeak')}
          </p>
          {c.headroomPct != null && (
            <div className="k-stack" style={{ gap: 6 }}>
              <div className="k-row" style={{ justifyContent: 'space-between' }}><span className="k-muted" style={{ fontSize: 13 }}>{t('infra.capacity.headroom')}</span><strong>{fmtNum(c.headroomPct, lang, 0)}%</strong></div>
              <Meter pct={100 - c.headroomPct} tone={tone === 'default' ? 'accent' : tone} label={t('infra.capacity.headroom')} />
            </div>
          )}
          {c.estimatedMaxBusinesses != null && <p style={{ margin: 0, fontSize: 14 }}>{t('infra.capacity.businesses', { max: num(c.estimatedMaxBusinesses, lang), active: num(c.activeBusinesses, lang) })}</p>}
        </div>
      )}
      <p className="k-muted" style={{ fontSize: 12, margin: '10px 0 0' }}>{t('infra.capacity.concurrency', { n: c.aiConcurrency })}{c.estimatedMaxBusinesses == null ? ` · ${t('infra.capacity.active', { n: num(c.activeBusinesses, lang) })}` : ''}</p>
    </Card>
  );
}

function QueuesCard({ snapshot: d }: { snapshot: InfraSnapshot }) {
  const { t, i18n } = useTranslation('admin');
  const lang = i18n.language;
  const cols = ['waiting', 'active', 'delayed', 'failed'] as const;
  return (
    <Card title={t('infra.queues.title')} padded={false} className="k-infra-flush">
      {d.queues.length === 0 ? <p className="k-muted" style={{ padding: '0 20px 20px', margin: 0 }}>{t('infra.noData')}</p> : (
        <div className="k-table__wrap">
          <table className="k-table k-infra-table">
            <thead><tr><th>{t('infra.queues.name')}</th><th>{t('infra.queues.status')}</th>{cols.map((c) => <th key={c} className="k-infra-num">{t(`infra.queues.${c}`)}</th>)}<th className="k-infra-num">{t('infra.queues.oldest')}</th></tr></thead>
            <tbody>
              {d.queues.map((q) => (
                <tr key={q.name} className={`k-infra-row--${q.status}`}>
                  <td data-label={t('infra.queues.name')}><strong className="k-mono">{q.name}</strong></td>
                  <td data-label={t('infra.queues.status')}><StatusChip status={q.status} /></td>
                  {cols.map((c) => <td key={c} data-label={t(`infra.queues.${c}`)} className="k-infra-num" style={c === 'failed' && q.failed > 0 ? { color: 'var(--color-danger)', fontWeight: 600 } : undefined}>{num(q[c], lang)}</td>)}
                  <td data-label={t('infra.queues.oldest')} className="k-infra-num">{q.oldestWaitSec == null ? '—' : q.oldestWaitSec < 60 ? fmtUnit(q.oldestWaitSec, 's', lang) : fmtUptime(q.oldestWaitSec)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function ProcessesCard({ snapshot: d }: { snapshot: InfraSnapshot }) {
  const { t, i18n } = useTranslation('admin');
  const lang = i18n.language;
  return (
    <Card title={t('infra.processes.title')} subtitle={t('infra.processes.subtitle')} padded={false} className="k-infra-flush">
      {d.processes.length === 0 ? <p className="k-muted" style={{ padding: '0 20px 20px', margin: 0 }}>{t('infra.processes.none')}</p> : (
        <div className="k-table__wrap">
          <table className="k-table k-infra-table">
            <thead><tr><th>{t('infra.processes.instance')}</th><th>{t('infra.processes.role')}</th><th className="k-infra-num">{t('infra.processes.memory')}</th><th className="k-infra-num">CPU</th><th className="k-infra-num">{t('infra.processes.eventLoop')}</th><th className="k-infra-num">{t('infra.processes.uptime')}</th><th>{t('infra.processes.at')}</th></tr></thead>
            <tbody>
              {d.processes.map((p) => (
                <tr key={p.instanceId}>
                  <td data-label={t('infra.processes.instance')}><span className="k-mono" title={p.instanceId}>{p.instanceId}</span></td>
                  <td data-label={t('infra.processes.role')}><Chip tone={p.role === 'api' ? 'outline' : 'neutral'}>{t(`infra.processes.roles.${p.role}`, { defaultValue: p.role })}</Chip></td>
                  <td data-label={t('infra.processes.memory')} className="k-infra-num">{fmtUnit(p.rssMb, 'MB', lang)} <span className="k-muted">({t('infra.processes.heap')} {fmtUnit(p.heapUsedMb, 'MB', lang)})</span></td>
                  <td data-label="CPU" className="k-infra-num">{fmtUnit(p.cpuPct, '%', lang)}</td>
                  <td data-label={t('infra.processes.eventLoop')} className="k-infra-num">{fmtUnit(p.eventLoopP50Ms, 'ms', lang)} / {fmtUnit(p.eventLoopP99Ms, 'ms', lang)}</td>
                  <td data-label={t('infra.processes.uptime')} className="k-infra-num">{fmtUptime(p.uptimeSec)}</td>
                  <td data-label={t('infra.processes.at')}><span className="k-muted">{when(p.at, lang, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function LlmCard({ llm, thresholds }: { llm: InfraLlm[]; thresholds: Thresholds | undefined }) {
  const { t, i18n } = useTranslation('admin');
  const lang = i18n.language;
  const band = getBand(thresholds, 'llmRemainingRequestsPct');
  const toneFor = (pct: number) => (band ? (pct <= band.red ? 'danger' : pct <= band.yellow ? 'warn' : 'accent') : 'accent');
  const bar = (label: string, remaining: number | null | undefined, limit: number | null | undefined) => {
    if (remaining == null || limit == null || limit <= 0) return <div className="k-row" style={{ justifyContent: 'space-between', fontSize: 13 }}><span className="k-muted">{label}</span><span className="k-muted">—</span></div>;
    const pct = (remaining / limit) * 100;
    return (
      <div className="k-stack" style={{ gap: 4 }}>
        <div className="k-row" style={{ justifyContent: 'space-between', fontSize: 13, gap: 8, flexWrap: 'wrap' }}><span className="k-muted">{label}</span><span>{t('infra.llm.remainingOf', { remaining: num(remaining, lang), limit: num(limit, lang) })} <span className="k-muted">({fmtNum(pct, lang, 0)}%)</span></span></div>
        <Meter pct={pct} tone={toneFor(pct)} label={label} />
      </div>
    );
  };
  return (
    <Card title={t('infra.llm.title')} subtitle={t('infra.llm.subtitle')}>
      {llm.length === 0 ? <span className="k-muted">{t('infra.noData')}</span> : (
        <div className="k-stack" style={{ gap: 18 }}>
          {llm.map((p) => (
            <div key={p.provider} className="k-stack" style={{ gap: 10 }}>
              <div className="k-row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <strong>{t(`integrations.labels.${p.provider}`, { defaultValue: p.provider })}</strong>
                <StatusChip status={p.status} />
              </div>
              {bar(t('infra.llm.requests'), p.requestsRemaining, p.requestsLimit)}
              {(p.inputTokensLimit != null || p.inputTokensRemaining != null) && bar(t('infra.llm.inputTokens'), p.inputTokensRemaining, p.inputTokensLimit)}
              {(p.outputTokensLimit != null || p.outputTokensRemaining != null) && bar(t('infra.llm.outputTokens'), p.outputTokensRemaining, p.outputTokensLimit)}
              <div className="k-row" style={{ gap: 8, flexWrap: 'wrap', fontSize: 13 }}>
                <Chip tone={p.errors429LastHour > 0 ? 'warn' : 'neutral'}>429 · {num(p.errors429LastHour, lang)}</Chip>
                <Chip tone={p.errors5xxLastHour > 0 ? 'danger' : 'neutral'}>5xx · {num(p.errors5xxLastHour, lang)}</Chip>
                <span className="k-muted">{t('infra.llm.lastHour')}{p.at ? ` · ${t('infra.llm.readAt', { when: when(p.at, lang) })}` : ` · ${t('infra.llm.noHeaders')}`}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function ResourcesCard({ snapshot: d }: { snapshot: InfraSnapshot }) {
  const { t, i18n } = useTranslation('admin');
  const lang = i18n.language;
  const r = d.render;
  const row = (label: string, value: string) => <div className="k-kv__row" style={{ minHeight: 34 }}><span className="k-muted">{label}</span><span>{value}</span></div>;
  const pair = (a: number | null | undefined, b: number | null | undefined, unit: 'MB' | 'GB' | 'count') => a == null ? '—' : b == null ? fmtUnit(a, unit, lang) : `${fmtUnit(a, unit, lang)} / ${fmtUnit(b, unit, lang)}`;
  return (
    <Card title={t('infra.resources.title')}>
      <div className="k-section-label" style={{ marginBottom: 4 }}>Postgres</div>
      <div className="k-kv">
        {row(t('infra.resources.dbSize'), fmtUnit(d.postgres.dbSizeMb, 'MB', lang))}
        {row(t('infra.resources.connections'), `${num(d.postgres.connections, lang)} / ${num(d.postgres.maxConnections, lang)}`)}
        {row(t('infra.resources.cacheHit'), fmtUnit(d.postgres.cacheHitPct, '%', lang))}
      </div>
      {d.postgres.topTables.length > 0 && (
        <details className="k-infra-details">
          <summary>{t('infra.resources.topTables')}</summary>
          {d.postgres.topTables.map((tb) => <div key={tb.name} className="k-row" style={{ justifyContent: 'space-between', fontSize: 13, padding: '3px 0', gap: 8 }}><span className="k-mono" style={{ minWidth: 0 }}>{tb.name}</span><span>{fmtUnit(tb.sizeMb, 'MB', lang)}</span></div>)}
        </details>
      )}
      <div className="k-section-label" style={{ margin: '14px 0 4px' }}>Redis</div>
      <div className="k-kv">
        {row(t('infra.resources.memory'), pair(d.redis.usedMb, d.redis.maxMb, 'MB'))}
        {row(t('infra.resources.peak'), fmtUnit(d.redis.peakMb, 'MB', lang))}
        {row(t('infra.resources.fragmentation'), d.redis.fragmentation == null ? '—' : fmtNum(d.redis.fragmentation, lang, 2))}
        {row(t('infra.resources.clients'), num(d.redis.clients, lang))}
      </div>
      {r.configured && (r.api || r.postgres || r.keyValue) && (
        <>
          <div className="k-section-label" style={{ margin: '14px 0 4px' }}>Render</div>
          <div className="k-kv">
            {r.api && row(`API · ${r.api.plan}`, [`CPU ${fmtUnit(r.api.cpuPct, '%', lang)}`, `${t('infra.resources.memShort')} ${pair(r.api.memoryMb, r.api.memoryLimitMb, 'MB')}`, r.api.httpP95Ms != null ? `HTTP p95 ${fmtUnit(r.api.httpP95Ms, 'ms', lang)}` : null].filter(Boolean).join(' · '))}
            {r.postgres && row(`Postgres · ${r.postgres.plan}`, [r.postgres.cpuPct != null ? `CPU ${fmtUnit(r.postgres.cpuPct, '%', lang)}` : null, r.postgres.memoryMb != null ? `${t('infra.resources.memShort')} ${fmtUnit(r.postgres.memoryMb, 'MB', lang)}` : null, r.postgres.diskUsedGb != null ? `${t('infra.resources.disk')} ${pair(r.postgres.diskUsedGb, r.postgres.diskCapacityGb, 'GB')}` : null, r.postgres.connections != null ? `${t('infra.resources.connShort')} ${num(r.postgres.connections, lang)}` : null].filter(Boolean).join(' · ') || '—')}
            {r.keyValue && row(`Key Value · ${r.keyValue.plan}`, [r.keyValue.memoryMb != null ? `${t('infra.resources.memShort')} ${pair(r.keyValue.memoryMb, r.keyValue.memoryLimitMb, 'MB')}` : null, r.keyValue.connections != null ? `${t('infra.resources.connShort')} ${pair(r.keyValue.connections, r.keyValue.maxConnections, 'count')}` : null].filter(Boolean).join(' · ') || '—')}
          </div>
        </>
      )}
    </Card>
  );
}

const DAYS = ['1', '7', '30', '90'] as const;
function TrendsCard({ thresholds }: { thresholds: Thresholds | undefined }) {
  const { t, i18n } = useTranslation('admin');
  const lang = i18n.language;
  const [metric, setMetric] = useState(TREND_METRICS[0]!.key);
  const [days, setDays] = useState<(typeof DAYS)[number]>('7');
  const [asTable, setAsTable] = useState(false);
  const h = useAdminQuery<InfraHistory>('/admin/infra/history', { metric, days: Number(days) });
  const def = TREND_METRICS.find((m) => m.key === metric) ?? TREND_METRICS[0]!;
  const band = def.threshold ? getBand(thresholds, def.threshold) : null;
  const lower = def.threshold ? LOWER_WORSE.has(def.threshold) : false;
  const points = useMemo(() => (h.data?.metric === metric ? h.data.points : []).map((p) => ({ t: Date.parse(p.t), value: p.value, status: p.status })).filter((p) => Number.isFinite(p.t)), [h.data, metric]);
  const fmt = (v: number) => fmtUnit(v, def.unit, lang);
  const bands: ChartBand[] = band ? (lower ? [{ from: band.red, to: band.yellow, tone: 'warn' }, { from: -Infinity, to: band.red, tone: 'danger' }] : [{ from: band.yellow, to: band.red, tone: 'warn' }, { from: band.red, to: Infinity, tone: 'danger' }]) : [];
  const rules: ChartRule[] = band ? [{ value: band.yellow, tone: 'warn', label: t('infra.trends.yellow', { v: fmt(band.yellow) }) }, { value: band.red, tone: 'danger', label: t('infra.trends.red', { v: fmt(band.red) }) }] : [];
  const short = days === '1' ? { hour: '2-digit', minute: '2-digit' } as const : { day: '2-digit', month: '2-digit' } as const;
  const label = t(`infra.trends.metrics.${i18nPath(def.key)}`, { defaultValue: def.key });
  return (
    <Card title={t('infra.trends.title')} actions={<Button size="sm" variant="ghost" onClick={() => setAsTable(!asTable)}>{asTable ? t('infra.trends.showChart') : t('infra.trends.showTable')}</Button>}>
      <div className="k-row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 14 }}>
        <div style={{ flex: '1 1 220px', minWidth: 0, maxWidth: 360 }}>
          <Select aria-label={t('infra.trends.metric')} value={metric} onChange={(e) => setMetric(e.target.value)}>{TREND_METRICS.map((m) => <option key={m.key} value={m.key}>{t(`infra.trends.metrics.${i18nPath(m.key)}`, { defaultValue: m.key })}</option>)}</Select>
        </div>
        <Segmented value={days} onChange={setDays} ariaLabel={t('infra.trends.period')} options={DAYS.map((d) => ({ value: d, label: t('infra.trends.days', { count: Number(d) }) }))} />
      </div>
      {h.isLoading ? <Skeleton lines={1} height={220} /> : asTable ? (
        <div className="k-infra-scroll">
          <table className="k-table k-infra-table k-infra-table--compact">
            <thead><tr><th>{t('infra.trends.when')}</th><th className="k-infra-num">{label}</th><th>{t('infra.queues.status')}</th></tr></thead>
            <tbody>{[...points].reverse().map((p) => <tr key={p.t}><td data-label={t('infra.trends.when')}>{when(new Date(p.t), lang)}</td><td data-label={label} className="k-infra-num">{p.value == null ? '—' : fmt(p.value)}</td><td data-label={t('infra.queues.status')}><StatusChip status={p.status} /></td></tr>)}</tbody>
          </table>
          {points.length === 0 && <p className="k-muted" style={{ fontSize: 13 }}>{t('infra.trends.empty')}</p>}
        </div>
      ) : (
        <LineChart
          points={points}
          ariaLabel={`${label} · ${t('infra.trends.days', { count: Number(days) })}`}
          formatValue={fmt}
          formatTime={(ts) => when(new Date(ts), lang)}
          formatTick={(ts) => new Intl.DateTimeFormat(lang, short).format(new Date(ts))}
          bands={bands}
          rules={rules}
          yMin={!lower && def.unit !== 'text' ? 0 : undefined}
          loading={h.isFetching}
          empty={t('infra.trends.empty')}
        />
      )}
      <p className="k-muted" style={{ fontSize: 12, margin: '10px 0 0' }}>{band ? t(lower ? 'infra.trends.bandsLower' : 'infra.trends.bandsHigher', { yellow: fmt(band.yellow), red: fmt(band.red) }) : def.threshold ? t('infra.trends.bandsLoading') : t('infra.trends.noBands')}</p>
    </Card>
  );
}

function ProjectionsCard({ snapshot: d }: { snapshot: InfraSnapshot }) {
  const { t, i18n } = useTranslation('admin');
  const lang = i18n.language;
  const en = isEnglish(lang);
  const unitOf = (key: string) => TREND_METRICS.find((m) => m.key === key)?.unit;
  return (
    <Card title={t('infra.projections.title')} subtitle={t('infra.projections.subtitle')}>
      {d.projections.length === 0 ? <span className="k-muted">{t('infra.noData')}</span> : (
        <div className="k-kv">
          {d.projections.map((p) => {
            const unit = unitOf(p.key);
            const outcome = (
              <span className="k-row" style={{ gap: 6, flexWrap: 'wrap' }}>
                {p.insufficientData ? <Chip tone="neutral">{t('infra.projections.insufficient')}</Chip>
                  : p.daysToYellow == null && p.daysToRed == null ? <Chip tone="accent">{t('infra.projections.stable')}</Chip>
                  : <>
                      {p.daysToYellow != null && <Chip tone="warn">{p.daysToYellow <= 0 ? t('infra.projections.yellowNow') : t('infra.projections.yellowIn', { count: Math.ceil(p.daysToYellow) })}</Chip>}
                      {p.daysToRed != null && <Chip tone="danger">{p.daysToRed <= 0 ? t('infra.projections.redNow') : t('infra.projections.redIn', { count: Math.ceil(p.daysToRed) })}</Chip>}
                    </>}
              </span>
            );
            return (
              <div key={p.key} className="k-kv__row" style={{ minHeight: 44, flexWrap: 'wrap', gap: 8 }}>
                <span><span>{en ? p.labelEn || p.label : p.label}</span> <strong style={{ marginLeft: 6 }}>{unit ? fmtUnit(p.current, unit, lang) : fmtNum(p.current, lang)}</strong></span>
                {outcome}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function RecommendationsCard({ recommendations }: { recommendations: InfraRecommendation[] }) {
  const { t, i18n } = useTranslation('admin');
  const lang = i18n.language;
  const en = isEnglish(lang);
  const list = recommendations.map((r, i) => ({ r, i })).sort((a, b) => SEVERITY_RANK[b.r.severity] - SEVERITY_RANK[a.r.severity] || a.i - b.i).map((x) => x.r);
  const sevStatus: Record<InfraRecommendation['severity'], InfraStatus> = { red: 'red', yellow: 'yellow', info: 'unknown' };
  return (
    <Card title={t('infra.recommendations.title')}>
      {list.length === 0 ? <span className="k-muted">{t('infra.recommendations.none')}</span> : (
        <div className="k-stack" style={{ gap: 10 }}>
          {list.map((r) => (
            <div key={r.id} className={`k-infra-rec k-infra-rec--${r.severity}`}>
              <div className="k-row" style={{ justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <strong style={{ minWidth: 0 }}>{en ? r.titleEn || r.title : r.title}</strong>
                <StatusChip status={sevStatus[r.severity]} label={t(`infra.recommendations.severity.${r.severity}`)} />
              </div>
              <p style={{ margin: '6px 0 0', fontSize: 13 }}>{en ? r.reasonEn || r.reason : r.reason}</p>
              {r.action && <p style={{ margin: '6px 0 0', fontSize: 13 }}><span className="k-muted">{t('infra.recommendations.action')}:</span> {r.action}</p>}
              {(r.currentPlan || r.nextPlan) && <p style={{ margin: '6px 0 0', fontSize: 13 }}><span className="k-muted">{t('infra.recommendations.plan')}:</span> <strong>{r.currentPlan ?? '—'}</strong> → <strong>{r.nextPlan ?? '—'}</strong></p>}
              {(r.monthlyDeltaBrl != null || r.monthlyDeltaUsd != null) && (
                <p style={{ margin: '6px 0 0', fontSize: 13 }}>
                  <span className="k-muted">{t('infra.recommendations.monthly')}:</span> <strong>+{r.monthlyDeltaBrl != null ? money(r.monthlyDeltaBrl, lang) : usd(r.monthlyDeltaUsd, lang)}</strong>
                  {r.monthlyDeltaBrl != null && r.monthlyDeltaUsd != null && <span className="k-muted" style={{ fontSize: 11 }}> ({usd(r.monthlyDeltaUsd, lang)})</span>}
                  {r.perActiveBusinessBrl != null && <span className="k-muted"> · {t('infra.recommendations.perBusiness', { v: money(r.perActiveBusinessBrl, lang) })}</span>}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function MetaCard({ snapshot: d }: { snapshot: InfraSnapshot }) {
  const { t, i18n } = useTranslation('admin');
  const lang = i18n.language;
  const rows = [...d.meta].sort((a, b) => STATUS_RANK[b.status] - STATUS_RANK[a.status] || a.businessName.localeCompare(b.businessName));
  return (
    <Card title={t('infra.meta.title')} subtitle={t('infra.meta.subtitle')} padded={false} className="k-infra-flush">
      {rows.length === 0 ? <p className="k-muted" style={{ padding: '0 20px 20px', margin: 0 }}>{t('infra.meta.none')}</p> : (
        <div className="k-table__wrap">
          <table className="k-table k-infra-table">
            <thead><tr><th>{t('infra.meta.business')}</th><th>{t('infra.meta.phone')}</th><th>{t('infra.meta.qualityLabel')}</th><th>{t('infra.meta.tier')}</th><th>{t('infra.meta.throughput')}</th><th>{t('infra.meta.checkedAt')}</th><th>{t('infra.meta.error')}</th></tr></thead>
            <tbody>
              {rows.map((m) => (
                <tr key={`${m.businessId}-${m.phone ?? ''}`}>
                  <td data-label={t('infra.meta.business')}><Link to={`/negocios/${m.businessId}`}>{m.businessName}</Link> <span className="k-muted" style={{ fontSize: 12 }}>{m.provider}</span></td>
                  <td data-label={t('infra.meta.phone')}>{m.phone ?? '—'}</td>
                  <td data-label={t('infra.meta.qualityLabel')}><QualityChip rating={m.qualityRating} /></td>
                  <td data-label={t('infra.meta.tier')}>{m.messagingTier ?? '—'}</td>
                  <td data-label={t('infra.meta.throughput')}>{m.throughput ?? '—'}</td>
                  <td data-label={t('infra.meta.checkedAt')}>{when(m.checkedAt, lang)}</td>
                  <td data-label={t('infra.meta.error')} style={{ color: m.error ? 'var(--color-danger)' : undefined, overflowWrap: 'anywhere', fontSize: 13 }}>{m.error ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
